/**
 * Calculate the next date for the sentiment check.
 *
 * @param openDate - open date of the assessment
 * @param interval - The interval for the sentiment check.
 * @param endDate - The end date of the assessment
 */
export function calculateNextDate(
  openDate: string | null,
  interval: number,
  endDate: string | null
): string | null {
  const currentDate = new Date();
  if (!openDate || !endDate) return null;

  const release = new Date(openDate);
  const due = new Date(endDate);
  const nextIntervalStart = nextIntervalStartDate(release, interval, due);

  // if due date is passed then return null
  if (currentDate > due || !nextIntervalStart) return null;

  // if current date is before the release date then return the release date
  if (currentDate < release) return release.toISOString();

  // if the next interval start is before the current date then return the current date
  if (nextIntervalStart < currentDate) return currentDate.toISOString();

  // return the next interval start date
  return nextIntervalStart.toISOString();
}

/**
 * Determine the next date for the sentiment check.
 * As lastCheck is already performed in the interval it will return nextDate
 * and if not then it will return current date.
 *
 * @param lastCheckDate - The date of the last check.
 * @param nextDate - The date of the next check.
 * @param interval - The interval for the sentiment check.
 * @param openDate - The open date of the assessment.
 * @returns The next date for the sentiment check.
 */
export function determineNextDate(
  lastCheckDate: string | null,
  nextDate: string,
  interval: number,
  openDate: string
): string | null {
  const prevDate = new Date(
    new Date(nextDate).getTime() - interval * 60 * 60 * 1000
  );
  const openDateObj = new Date(openDate);
  const currentDate = new Date();

  // Check if prevDate == openDate, return nextDate
  if (prevDate.getTime() === openDateObj.getTime()) return nextDate;

  // Check if prevDate > openDate
  if (prevDate.getTime() > openDateObj.getTime()) {
    // Case 1: Check if lastCheckDate doesn't exist, return current date
    if (!lastCheckDate) return currentDate.toISOString();
    // Case 2: Check if lastCheckDate doesn't lie between prevDate and nextDate, return current date
    const lastCheckDateObj = new Date(lastCheckDate);
    if (
      lastCheckDateObj.getTime() < prevDate.getTime() ||
      lastCheckDateObj.getTime() > new Date(nextDate).getTime()
    ) {
      return currentDate.toISOString();
    }
  }

  return nextDate;
}

/**
 * Caclculate the next interval start date for the sentiment check.
 *
 * If the next interval start date is past the end date, it returns null.
 *
 * @param openDate - open date of the assessment
 * @param intervalInHrs - The interval for the sentiment check.
 * @param endDate - The end date of the assessment
 */
export function nextIntervalStartDate(
  openDate: Date,
  intervalInHrs: number,
  endDate: Date
): Date | null {
  const currentDate = new Date();

  if (currentDate > endDate) return null;

  const intervalInMs = intervalInHrs * 60 * 60 * 1000;

  // Calculate how much time has passed since open date
  const timeSinceOpen = currentDate.getTime() - openDate.getTime();

  const nextIntervalIndex = Math.floor(timeSinceOpen / intervalInMs) + 1;

  // Calculate the exact time of the next interval
  const nextIntervalStart =
    openDate.getTime() + nextIntervalIndex * intervalInMs;

  // Check if the next interval is within the end date
  if (nextIntervalStart > endDate.getTime()) return null;

  return new Date(nextIntervalStart);
}
